/**
 * Left Toolbar Component
 * Node palette with drag and drop
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useWorkflowStore } from '@flow-studio/store'; // import { workflowStore';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import { useNodeDrag } from '../../hooks/useNodeDrag';
import { NodeType, NodeCategory } from '@flow-studio/types';
import { HelpModal } from '../Modals/HelpModal';
import { NodeTemplateEditModal } from '../Modals/NodeTemplateEditModal';
import { CurationFilter, useCurationFilter } from './CurationFilter';
import { CustomDropdown } from '../Support/CustomDropdown';
import { InfoIcon } from '@bizfirst/common-react';
import { NodeLeftIcon, NodeBrandImage, resolveFromNodeType } from '../NodeIcon';

export const LeftToolbar: React.FC = () => {
  const { nodeTypes, nodeCategories } = useWorkflowStore();
  const { leftPanelOpen, theme } = useUIStore();
  const { onDragStart } = useNodeDrag();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [hoveredNodeCode, setHoveredNodeCode] = useState<string | null>(null);
  const [hoveredRect, setHoveredRect] = useState<DOMRect | null>(null);
  const [openDropdownCode, setOpenDropdownCode] = useState<string | null>(null);
  const nodeKey = (nt: NodeType): string => String(nt.id);
  const [nodeTemplateEdit, setNodeTemplateEdit] = useState<NodeType | null>(null);
  const { curation, toggleCuration, matchesCuration } = useCurationFilter();

  // Close palette dropdown when clicking anywhere outside it.
  // React Flow's pane calls stopImmediatePropagation() on its own mousedown
  // handler, so a plain document listener never sees clicks on the canvas —
  // also listen for the flow-studio:pane-click event the canvas dispatches
  // instead (see WorkflowCanvas.tsx onPaneClick).
  React.useEffect(() => {
    if (!openDropdownCode) return;
    const handler = () => setOpenDropdownCode(null);
    document.addEventListener('mousedown', handler);
    window.addEventListener('flow-studio:pane-click', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('flow-studio:pane-click', handler);
    };
  }, [openDropdownCode]);

  const [helpModal, setHelpModal] = useState<{
    isOpen: boolean;
    entity: NodeType | NodeCategory | null;
    entityType: 'nodeType' | 'category';
  }>({
    isOpen: false,
    entity: null,
    entityType: 'nodeType',
  });

  // Add custom scrollbar styles
  React.useEffect(() => {
    const styleId = 'left-toolbar-scrollbar-styles';
    let style = document.getElementById(styleId) as HTMLStyleElement;

    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }

    // Update styles based on theme
    style.textContent = `
      .left-toolbar-nodes-list::-webkit-scrollbar {
        width: 8px;
      }
      .left-toolbar-nodes-list::-webkit-scrollbar-track {
        background: transparent;
        border-right: 1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'};
      }
      .left-toolbar-nodes-list::-webkit-scrollbar-thumb {
        background: ${theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
        border-radius: 4px;
        border-right: 1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'};
      }
      .left-toolbar-nodes-list::-webkit-scrollbar-thumb:hover {
        background: ${theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
      }
    `;
  }, [theme]);

  if (!leftPanelOpen) {
    return null;
  }

  // Prepare dropdown options
  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...nodeCategories.map(cat => ({
      value: cat.code,
      label: cat.displayName,
    })),
  ];

  const filteredNodes = nodeTypes.filter((nodeType) => {
    const matchesSearch = nodeType.displayName
      .toLowerCase()
      .includes(searchTerm.toLowerCase()) ||
      nodeType.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || nodeType.category === selectedCategory;
    return matchesSearch && matchesCategory && matchesCuration(nodeType);
  });

  const toolbarStyle: React.CSSProperties = {
    background: theme === 'dark' ? 'rgba(2, 6, 23, 0.7)' : '#fff',
    backdropFilter: 'blur(16px)',
    borderRight: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    // Width comes from the .left-toolbar rule in workflowEditor.css (240px, narrower on phones).
  };

  const headerStyle: React.CSSProperties = {
    padding: '6.5px 12px',
    borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.07)' : '#e5e7eb'}`,
    background: theme === 'dark' ? 'linear-gradient(135deg, #1a4d2a 0%, #0d2516 100%)' : 'linear-gradient(135deg, #309B48 0%, #1E5F2E 100%)',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    color: '#ffffff',
  };

  const searchStyle: React.CSSProperties = {
    width: '100%',
    padding: '4px 8px',
    border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'}`,
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    background: theme === 'dark' ? 'rgba(0, 0, 0, 0.3)' : '#fff',
    color: theme === 'dark' ? '#e5e7eb' : '#111827',
  };

  const categoryStyle: React.CSSProperties = {
    padding: '4px 8px',
    borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'}`,
    flexShrink: 0,
  };

  const nodesListStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '4px',
    borderRight: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'}`,
  };

  const nodeItemStyle: React.CSSProperties = {
    padding: '6px 8px',
    margin: '2px 0',
    background: theme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f9fafb',
    border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#e5e7eb'}`,
    borderRadius: '6px',
    cursor: 'grab',
    transition: 'all 0.2s',
  };

  const nodeHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: 600,
    fontSize: '13px',
    marginBottom: '4px',
    color: theme === 'dark' ? '#f3f4f6' : '#111827',
  };

  const nodeDescStyle: React.CSSProperties = {
    fontSize: '11px',
    color: theme === 'dark' ? '#9ca3af' : '#6b7280',
  };

  return (
    <>
    <div className="left-toolbar" style={toolbarStyle}>
        {/* Header — title + category select inline; paddingRight clears the collapse tab */}
        <div className="left-toolbar-header" style={{ ...headerStyle, display: 'flex', alignItems: 'center', gap: '6px', paddingRight: '28px' }}>
          <h3 className="left-toolbar-title" style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: 'var(--studio-text, #ffffff)', flexShrink: 0 }}>
            Nodes
          </h3>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
            <CustomDropdown
              options={categoryOptions}
              value={selectedCategory}
              onChange={setSelectedCategory}
              placeholder="All Categories"
              triggerStyle={{
                width: 'auto',
                minWidth: '80px',
                maxWidth: '120px',
                padding: '2px 10px',
                background: selectedCategory
                  ? 'rgba(255,255,255,0.22)'
                  : 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.35)',
                borderRadius: '12px',
                color: 'var(--studio-text, #ffffff)',
                fontSize: '11px',
                fontWeight: 500,
                boxShadow: 'none',
              }}
            />
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: '6px 8px', flexShrink: 0 }}>
          <input
            className="left-toolbar-search"
            type="text"
            placeholder="Search nodes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchStyle}
          />
        </div>

        {/* Curation filter */}
        <CurationFilter curation={curation} onToggle={toggleCuration} theme={theme} />

        {/* Nodes List */}
        <div className="left-toolbar-nodes-list" style={nodesListStyle}>
          {filteredNodes.map((nodeType: NodeType, idx: number) => {
            const isDisabled = !!(nodeType as any).disableOnToolbar;
            const idxKey = String(idx);
            return (
            <div
              key={nodeKey(nodeType)}
              className="node-palette-item"
              style={{
                ...nodeItemStyle,
                ...(isDisabled ? { opacity: 0.45, cursor: 'not-allowed' } : {}),
              }}
              draggable={!isDisabled}
              onDragStart={isDisabled ? undefined : (e) => onDragStart(e, nodeType)}
              onMouseEnter={isDisabled ? undefined : (e) => {
                setHoveredNodeCode(idxKey);
                setHoveredRect((e.currentTarget as HTMLElement).getBoundingClientRect());
                (e.currentTarget as HTMLElement).style.borderColor = '#3A8C45';
                (e.currentTarget as HTMLElement).style.background = 'rgba(58, 140, 69, 0.1)';
              }}
              onMouseLeave={isDisabled ? undefined : (e) => {
                setHoveredNodeCode(null);
                setHoveredRect(null);
                (e.currentTarget as HTMLElement).style.borderColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#e5e7eb';
                (e.currentTarget as HTMLElement).style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f9fafb';
              }}
              title={isDisabled ? 'Not available' : undefined}
            >
              <div className="node-palette-item-header" style={nodeHeaderStyle}>
                {(() => {
                  const iconColor = theme === 'dark' ? '#94a3b8' : '#64748b';
                  const { icon, headerImageUrl, category } = resolveFromNodeType(nodeType);
                  return (
                    <>
                      <span className="node-palette-item-icon" style={{ display: 'flex', alignItems: 'center' }}>
                        <NodeLeftIcon icon={icon} category={category} size={16} color={iconColor} />
                      </span>
                      <span
                        className="node-palette-item-name"
                        title={nodeType.displayName}
                        style={{ flex: 1, fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                      >
                        {nodeType.displayName}
                      </span>
                      <NodeBrandImage headerImageUrl={headerImageUrl} size={16} />
                    </>
                  );
                })()}

                {/* ··· submenu: visible when item is hovered OR its dropdown is open (not for disabled nodes) */}
                {!isDisabled && (hoveredNodeCode === idxKey || openDropdownCode === idxKey) && (
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setOpenDropdownCode(openDropdownCode === idxKey ? null : idxKey);
                      }}
                      style={{
                        background: openDropdownCode === idxKey
                          ? 'rgba(58, 140, 69, 0.3)'
                          : (theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'),
                        border: `1px solid ${openDropdownCode === idxKey ? '#3A8C45' : 'rgba(255,255,255,0.15)'}`,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        padding: '2px 6px',
                        fontSize: '14px',
                        color: openDropdownCode === idxKey ? '#3A8C45' : (theme === 'dark' ? 'rgba(255,255,255,0.6)' : '#6b7280'),
                        letterSpacing: '2px',
                        lineHeight: 1,
                      }}
                      title="More options"
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                    >
                      ···
                    </button>

                    {openDropdownCode === idxKey && (
                      <div
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          right: 0,
                          zIndex: 500,
                          background: '#1e293b',
                          border: '1px solid rgba(255,255,255,0.12)',
                          borderRadius: '6px',
                          minWidth: '130px',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                          overflow: 'hidden',
                        }}
                      >
                        {/* Help */}
                        {(() => {
                          const { helpUrl } = resolveFromNodeType(nodeType);
                          const sharedStyle = { display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.8)', fontSize: '12px', cursor: 'pointer', textAlign: 'left' as const, textDecoration: 'none' };
                          return helpUrl ? (
                            <button
                              style={sharedStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdownCode(null);
                                window.open(helpUrl, 'flow-studio-help-window');
                              }}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)'; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                            >
                              <InfoIcon size={13} />
                              Help
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdownCode(null);
                                setHelpModal({ isOpen: true, entity: nodeType, entityType: 'nodeType' });
                              }}
                              style={sharedStyle}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)'; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                            >
                              <InfoIcon size={13} />
                              Help
                            </button>
                          );
                        })()}

                        {/* Edit JSON — opens NodeTemplateEditModal */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdownCode(null);
                            setNodeTemplateEdit(nodeType);
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.8)', fontSize: '12px', cursor: 'pointer', textAlign: 'left' }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                        >
                          <span style={{ fontSize: '13px' }}>{'{ }'}</span>
                          Edit JSON
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Description rendered as a fixed-position tooltip to the right — see portal below */}
            </div>
          );})}
        </div>

      {/* Help Modal */}
      {helpModal.entity && (
        <HelpModal
          isOpen={helpModal.isOpen}
          onClose={() => setHelpModal({ isOpen: false, entity: null, entityType: 'nodeType' })}
          entity={helpModal.entity}
          entityType={helpModal.entityType}
        />
      )}

      {/* Node Template Edit Modal */}
      {nodeTemplateEdit && (
        <NodeTemplateEditModal
          key={nodeTemplateEdit.code}
          nodeType={nodeTemplateEdit}
          onClose={() => setNodeTemplateEdit(null)}
        />
      )}
      </div>

      {/* Description tooltip — fixed-position to the RIGHT of the toolbar */}
      {hoveredNodeCode && hoveredRect && (() => {
        const nt = filteredNodes[Number(hoveredNodeCode)];
        if (!nt?.description) return null;
        return createPortal(
          <div
            style={{
              position: 'fixed',
              left: hoveredRect.right + 10,
              top: hoveredRect.top,
              zIndex: 99998,
              maxWidth: '260px',
              background: theme === 'dark' ? 'rgba(15,23,42,0.97)' : 'rgba(255,255,255,0.97)',
              border: `1px solid ${theme === 'dark' ? 'rgba(58,140,69,0.35)' : '#e5e7eb'}`,
              borderRadius: '8px',
              padding: '10px 14px',
              boxShadow: theme === 'dark'
                ? '0 4px 20px rgba(0,0,0,0.6), 0 0 0 1px rgba(58,140,69,0.1)'
                : '0 4px 16px rgba(0,0,0,0.12)',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: 600, color: theme === 'dark' ? '#f1f5f9' : '#111827', marginBottom: '4px' }}>
              {nt.displayName}
            </div>
            <div style={{ fontSize: '11px', color: theme === 'dark' ? '#94a3b8' : '#6b7280', lineHeight: 1.5 }}>
              {nt.description}
            </div>
          </div>,
          document.body
        );
      })()}
    </>
  );
};
